import MarketingControlledButton from '@hellodarwin/app-core/lib/components/marketing-controlled-buttons';
import type { MentionCandidate } from '@hellodarwin/app-features/lib/features/conversations/types';
import { PaginatedGinStepsProvider } from '@hellodarwin/app-features/lib/features/gin-steps/paginated-gin-steps/provider';
import scrollElementIntoView from '@hellodarwin/app-features/lib/utils/dom/scroll-element-into-view';
import Button from '@hellodarwin/core/lib/components/common/button';
import Div from '@hellodarwin/core/lib/components/common/div';
import {
  AdminProfileInitialState,
  ProgramAdmin,
} from '@hellodarwin/core/lib/features/entities';
import type { GrantResult } from '@hellodarwin/core/lib/features/entities/grants-entities';
import type { Program } from '@hellodarwin/core/lib/features/entities/programs-entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Arrow from '@hellodarwin/icons/dist/icons/Arrow';
import { Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useAppSelector } from '../../../../app/app-hooks';
import { useClientAnalytics } from '../../../../features/analytics/use-client-analytics';
import {
  selectGinStepForm,
  selectGinStepsIsLoading,
  selectStepIdsByPageId,
  selectStepsById,
} from '../../../../features/api/slices/gin-steps-slice';
import { selectCompany } from '../../../../features/api/slices/profile-slice';
import {
  selectGrantById,
  selectProgramById,
} from '../../../../features/api/slices/programs-slice';
import ClientConversationBlock from './client-conversation-block';
import GinSteps from './gin-steps';
import GinStepsCustomClient from './gin-steps-custom-client';
import MainContentHeader from './main-content-header';
import ManagedProgramProgress from './main-content-header/managed-program-progress';
import StepsProgress from './main-content-header/steps-progress';
import { HeaderContainer } from './main-content-header/styles';
import { ManagedProgramWorkspace } from './managed-program-workspace';
import NoGinStepsAssistant from './no-gin-steps-assistant';
import { ApplicationContentStack } from './styles';

interface MainContentProps {
  programId: string;
  grantId: string;
  displayTitle?: string;
  program?: Program;
  programAdmins?: ProgramAdmin[];
  mentionCandidates?: MentionCandidate[];
  hasCustomApplicationAccess?: boolean;
  isManagedProgram?: boolean;
}

const MainContent = ({
  programId,
  grantId,
  displayTitle,
  program,
  programAdmins = [],
  mentionCandidates = [],
  hasCustomApplicationAccess,
  isManagedProgram = false,
}: MainContentProps) => {
  const { t } = useTranslations();
  const ginForm = useAppSelector(selectGinStepForm);
  const isGinStepsLoading = useAppSelector(selectGinStepsIsLoading);
  const stepIdsByPageId = useAppSelector(selectStepIdsByPageId);
  const stepsById = useAppSelector(selectStepsById);
  const theme = useTheme();
  const grant = useAppSelector(
    (state) => selectGrantById(state, grantId || '') as GrantResult | undefined,
  );
  const company = useAppSelector(selectCompany);
  const analytics = useClientAnalytics();
  const programById = useAppSelector((state) =>
    selectProgramById(state, programId),
  );
  const grantDisplayTitle = grant?.grant_display_title || displayTitle;
  const [showGoToConversation, setShowGoToConversation] = useState(false);
  const pageIds = useMemo(
    () => ginForm?.pages?.map((p) => p.page_id),
    [ginForm?.pages],
  );
  const hasNoGinSteps = useMemo(() => {
    if (!ginForm?.pages || isGinStepsLoading) return false;

    for (let i = 1; i <= 4; i++) {
      const steps = ginForm.pages[i]?.steps;
      if (steps && steps.length > 0) {
        return false;
      }
    }

    return true;
  }, [ginForm?.pages, isGinStepsLoading]);

  useEffect(() => {
    const hasTrackedConversation =
      isManagedProgram ||
      (hasCustomApplicationAccess === true && !hasNoGinSteps);
    if (!hasTrackedConversation) {
      setShowGoToConversation(false);
      return;
    }
    const el = document.getElementById('application-conversation');
    if (!el) {
      setShowGoToConversation(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowGoToConversation(!entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    hasCustomApplicationAccess,
    hasNoGinSteps,
    isManagedProgram,
    pageIds,
  ]);

  const headerAction = useMemo(() => {
    if (!grant?.grant_id) return null;

    return (
      <MarketingControlledButton
        type="grant-application"
        company={company}
        admin={AdminProfileInitialState}
        openMeetingModalClicked={() =>
          analytics.openMeetingModalClicked('grant-application')
        }
        size="small"
      />
    );
  }, [analytics, company, grant?.grant_id]);

  const header = (
    <HeaderContainer>
      <Space
        direction="vertical"
        size={theme.spacing.xl}
        style={{ width: '100%' }}
      >
        <MainContentHeader
          grantId={grantId}
          grantStatus={programById?.grant_status}
          title={displayTitle}
          program={program}
          programAdmins={programAdmins}
          headerAction={headerAction}
        />
        {!isManagedProgram && !hasNoGinSteps ? (
          <StepsProgress
            hasCustomApplicationAccess={hasCustomApplicationAccess}
          />
        ) : null}
        {isManagedProgram ? (
          <ManagedProgramProgress
            percentage={programById?.program_completion_percentage}
          />
        ) : null}
      </Space>
    </HeaderContainer>
  );

  return (
    <>
      {isManagedProgram ? (
        <ApplicationContentStack>
          {header}
          <ManagedProgramWorkspace
            programId={programId}
            mentionCandidates={mentionCandidates}
          />
        </ApplicationContentStack>
      ) : pageIds && stepsById ? (
        <PaginatedGinStepsProvider
          pageIds={pageIds}
          stepsById={stepsById}
          stepIdsByPageId={stepIdsByPageId}
        >
          <ApplicationContentStack>
            {header}
            {!hasCustomApplicationAccess &&
            hasNoGinSteps &&
            ginForm?.form_application_id &&
            grantDisplayTitle ? (
              <NoGinStepsAssistant
                entityNameDisplay={grantDisplayTitle}
                formApplicationId={ginForm.form_application_id}
              />
            ) : null}
            {!hasNoGinSteps && hasCustomApplicationAccess !== true ? (
              <GinSteps programId={programId} />
            ) : null}
            {hasCustomApplicationAccess ? (
              <>
                <GinStepsCustomClient programId={programId} />
                <Div
                  id="application-conversation"
                  style={{ paddingBottom: theme.spacing.xxl }}
                >
                  <ClientConversationBlock
                    programId={programId}
                    mentionCandidates={mentionCandidates}
                  />
                </Div>
              </>
            ) : null}
          </ApplicationContentStack>
        </PaginatedGinStepsProvider>
      ) : null}
      {showGoToConversation &&
      (hasCustomApplicationAccess || isManagedProgram) ? (
        <div
          style={{
            position: 'fixed',
            bottom: theme.spacing.xl,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            size="medium"
            shape="rounded"
            trailingIcon={<Arrow direction="left" size={16} />}
            style={{
              opacity: 0.75,
            }}
            onClick={() => {
              scrollElementIntoView({
                containerId: 'sidebar-page-body',
                elementId: 'application-conversation',
                padding: theme.spacing.xxxl,
              });
            }}
          >
            {t('application|conversationScrollButton')}
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default MainContent;
